const STORAGE_KEY = 'admin_api_key';
const STORAGE_CHANGE_EVENT = 'admin_api_key_changed';
const PURGE_FLAG_KEY = 'arspace:adminKeyPurged:v1';

export function getAdminApiKey(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(STORAGE_KEY);
}

export function setAdminApiKey(key: string | null) {
  if (typeof window === 'undefined') {
    return;
  }
  const oldKey = getAdminApiKey();
  if (key && key.trim().length > 0) {
    localStorage.setItem(STORAGE_KEY, key.trim());
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  
  // Dispatch custom event when admin key changes
  const newKey = getAdminApiKey();
  if (oldKey !== newKey) {
    window.dispatchEvent(new CustomEvent(STORAGE_CHANGE_EVENT, { 
      detail: { hasKey: !!newKey } 
    }));
  }
}

/**
 * One-time purge of a locally stored admin API key when the app is running
 * embedded in Shopify Admin.
 *
 * A key entered on one install could belong to a different (or synthetic
 * `settings-only.myshopify.com`) shop. Sent as `x-admin-api-key` it wins over
 * the App Bridge session token on the backend guard, so every request resolves
 * against the WRONG shop — empty product lists and 400s from billing checkout.
 * Session tokens always resolve to the real shop, so inside the iframe the
 * stored key is pure downside.
 */
export function purgeStaleAdminApiKey(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(PURGE_FLAG_KEY)) return;
    const existing = localStorage.getItem(STORAGE_KEY);
    localStorage.setItem(PURGE_FLAG_KEY, new Date().toISOString());
    if (existing) {
      setAdminApiKey(null);
      console.warn(
        '[adminKey] Removed stored admin API key — embedded sessions authenticate via the App Bridge session token.'
      );
    }
  } catch {
    // localStorage unavailable (private mode / partitioned iframe storage)
  }
}

/**
 * Drop a stored admin API key the server has definitively rejected.
 *
 * Distinct from `purgeStaleAdminApiKey`, which is a one-shot migration guarded
 * by a flag and only runs when App Bridge is present. This runs on evidence —
 * the backend told us this specific key matches no shop — and so is not
 * flag-guarded: a key that 401s will 401 forever, and re-sending it just turns
 * one failure into an unbreakable retry loop. That is exactly what happened
 * after a billing approval landed outside the iframe: no App Bridge, so the
 * client fell back to a key left over from a reinstall, and every request
 * failed identically with no self-correction.
 *
 * Only called for the non-reauthorizable case. A merely expired session is
 * recoverable by re-auth and must not cost the user their key.
 */
export function discardRejectedAdminApiKey(): void {
  if (typeof window === 'undefined') return;
  try {
    if (!localStorage.getItem(STORAGE_KEY)) return;
    setAdminApiKey(null);
    console.warn(
      '[adminKey] Server rejected the stored admin API key; removed it. ' +
        'Open the app from the Shopify admin, or create a new key in Settings.',
    );
  } catch {
    // localStorage unavailable (private mode / partitioned iframe storage)
  }
}

export function hasAdminApiKey(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const key = getAdminApiKey();
  return key !== null && key.trim().length > 0;
}



